// Applies per-page SEO tags (meta description/keywords, canonical, OG/Twitter,
// JSON-LD) over the defaults baked into public/index.html. This is a single
// SPA, so client-side navigation never reloads index.html — without this,
// tags set for one page (e.g. /p/history) would keep showing on the next
// page (e.g. back to /). applySeoTags returns a cleanup function that
// restores exactly what was there before; callers must run it on unmount.

type SeoTagData = {
    title?: string;
    description?: string;
    keywords?: string;
    canonicalUrl?: string;
    jsonLd?: Record<string, unknown>;
};

const getOrCreateMeta = (attr: "name" | "property", key: string): HTMLMetaElement => {
    let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    return el;
};

const getOrCreateCanonical = (): HTMLLinkElement => {
    let el = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", "canonical");
        document.head.appendChild(el);
    }
    return el;
};

export const applySeoTags = (data: SeoTagData): (() => void) => {
    const originalTitle = document.title;

    const descEl = getOrCreateMeta("name", "description");
    const originalDescription = descEl.getAttribute("content") || "";

    const keywordsEl = getOrCreateMeta("name", "keywords");
    const originalKeywords = keywordsEl.getAttribute("content") || "";

    const canonicalEl = getOrCreateCanonical();
    const originalCanonical = canonicalEl.getAttribute("href") || "";

    const ogTitleEl = getOrCreateMeta("property", "og:title");
    const originalOgTitle = ogTitleEl.getAttribute("content") || "";
    const ogDescEl = getOrCreateMeta("property", "og:description");
    const originalOgDescription = ogDescEl.getAttribute("content") || "";
    const ogUrlEl = getOrCreateMeta("property", "og:url");
    const originalOgUrl = ogUrlEl.getAttribute("content") || "";

    const twTitleEl = getOrCreateMeta("name", "twitter:title");
    const originalTwTitle = twTitleEl.getAttribute("content") || "";
    const twDescEl = getOrCreateMeta("name", "twitter:description");
    const originalTwDescription = twDescEl.getAttribute("content") || "";

    if (data.title) {
        document.title = data.title;
        ogTitleEl.setAttribute("content", data.title);
        twTitleEl.setAttribute("content", data.title);
    }
    if (data.description) {
        descEl.setAttribute("content", data.description);
        ogDescEl.setAttribute("content", data.description);
        twDescEl.setAttribute("content", data.description);
    }
    if (data.keywords) keywordsEl.setAttribute("content", data.keywords);
    if (data.canonicalUrl) {
        canonicalEl.setAttribute("href", data.canonicalUrl);
        ogUrlEl.setAttribute("content", data.canonicalUrl);
    }

    let jsonLdEl: HTMLScriptElement | null = null;
    if (data.jsonLd) {
        jsonLdEl = document.createElement("script");
        jsonLdEl.type = "application/ld+json";
        jsonLdEl.setAttribute("data-page-seo", "1");
        jsonLdEl.textContent = JSON.stringify(data.jsonLd);
        document.head.appendChild(jsonLdEl);
    }

    return () => {
        document.title = originalTitle;
        descEl.setAttribute("content", originalDescription);
        keywordsEl.setAttribute("content", originalKeywords);
        canonicalEl.setAttribute("href", originalCanonical);
        ogTitleEl.setAttribute("content", originalOgTitle);
        ogDescEl.setAttribute("content", originalOgDescription);
        ogUrlEl.setAttribute("content", originalOgUrl);
        twTitleEl.setAttribute("content", originalTwTitle);
        twDescEl.setAttribute("content", originalTwDescription);
        if (jsonLdEl) jsonLdEl.remove();
    };
};
